import React from 'react';
import JsonLd from '../../components/JsonLd';
import SEOHead from '../../components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
  BarChart2, TrendingUp, FileText, GitMerge, MessageSquare, CheckCircle2, ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const CaseTriagePage = () => {
  const benefits = [
    {
      icon: BarChart2,
      title: 'Intent, urgency, and sentiment classification in real time',
      description:
        'Every inbound message is classified for intent, urgency level, and customer sentiment the moment it arrives - enabling instant prioritization and routing decisions.',
    },
    {
      icon: FileText,
      title: 'Field extraction and auto-population into CRM',
      description:
        'Order IDs, account numbers, product references, and issue descriptions are extracted from unstructured messages and populated directly into CRM fields - eliminating manual data entry.',
    },
    {
      icon: MessageSquare,
      title: 'Agent assist suggestions with next-best actions',
      description:
        'Agents receive AI-generated reply suggestions and next-best action recommendations for every routed case, reducing handle time and improving response consistency.',
    },
  ];

  const capabilities = [
    'Integrate CRM/ticketing + knowledge base + agent desktop',
    'Define taxonomy: intents, queues, priorities, and SLA rules',
    'Configure extraction: order ID, account, product, urgency, and sentiment',
    'Set thresholds: auto-resolve vs assist vs human escalation',
    'Pilot assist-only → expand to containment and full automation',
  ];

  const features = [
    {
      icon: BarChart2,
      title: 'Intent Classification',
      desc: 'Classify every inbound message against a configurable intent taxonomy - routing to the right queue with the right priority from the first touch.',
      gradient: 'from-indigo-500 to-violet-500',
    },
    {
      icon: TrendingUp,
      title: 'Sentiment & Urgency Detection',
      desc: 'Detect frustrated, urgent, or at-risk customers in real time and escalate or re-prioritize automatically before the issue compounds.',
      gradient: 'from-violet-500 to-purple-500',
    },
    {
      icon: FileText,
      title: 'Field Extraction & Auto-Fill',
      desc: 'Extract structured fields from unstructured messages - order IDs, account numbers, product details - and auto-populate CRM records to eliminate manual entry.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: GitMerge,
      title: 'Intelligent Routing Engine',
      desc: 'Route cases to the correct queue, team, or agent based on intent, urgency, skill requirements, and SLA - with configurable fallback rules.',
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      icon: MessageSquare,
      title: 'Agent Assist Suggestions',
      desc: 'Surface AI-generated reply suggestions and recommended next steps to agents in real time - grounded in the knowledge base and case history.',
      gradient: 'from-rose-500 to-red-400',
    },
    {
      icon: CheckCircle2,
      title: 'Auto-Resolve for Routine Queries',
      desc: 'Fully resolve low-complexity, high-frequency inquiries automatically - delivering the answer and closing the case without agent involvement.',
      gradient: 'from-orange-400 to-amber-400',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'Inbound message arrives',
      desc: 'A customer message is received via voice, chat, or email and is immediately passed to the AI triage engine for analysis.',
    },
    {
      step: '02',
      title: 'AI classifies intent and urgency',
      desc: 'The message is classified against the configured intent taxonomy, urgency levels, and sentiment signals - in real time, before a human touches it.',
    },
    {
      step: '03',
      title: 'Extracts structured fields',
      desc: 'Key entities - order IDs, account numbers, product references, issue descriptions - are extracted from the message and structured for CRM population.',
    },
    {
      step: '04',
      title: 'Creates case and routes correctly',
      desc: 'A CRM case is created with pre-populated fields and routed to the correct queue or agent based on intent, urgency, skill, and SLA rules.',
    },
    {
      step: '05',
      title: 'Suggests reply and next steps - or resolves automatically',
      desc: 'For routed cases, the agent receives AI-generated reply suggestions and next-best actions. For routine queries, the AI resolves the case directly without agent involvement.',
    },
  ];

  const stats = [
    { value: '~68%', label: 'Tickets deflected automatically' },
    { value: '~88%', label: 'Routing accuracy' },
    { value: '−60%', label: 'AHT reduction (8min→3.2min)' },
    { value: '+35%', label: 'Agent efficiency gain' },
  ];

  const faqs = [
    {
      q: 'Can we start with assist-only before enabling auto-resolve?',
      a: 'Yes. The recommended deployment path is to start with agent assist - surfacing suggestions without taking autonomous action - to build confidence in AI accuracy before enabling containment. You control the threshold for when the AI resolves vs routes to an agent.',
    },
    {
      q: 'How do you prevent bad routing decisions?',
      a: 'The intent taxonomy, routing rules, and confidence thresholds are all configurable. Low-confidence classifications are sent to a default queue rather than being routed incorrectly. Routing performance is monitored continuously and the model improves from agent corrections.',
    },
    {
      q: 'Does it integrate with our existing CRM?',
      a: 'Yes. Digian.ai integrates with Salesforce, ServiceNow, Zendesk, Microsoft Dynamics, and other major CRM platforms. Field extraction mappings are configured to match your specific CRM schema and case object structure.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="useCaseCaseTriage" />
      <JsonLd schema={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Home","item":"https://digian.ai"},{"@type":"ListItem","position":2,"name":"Use Cases","item":"https://digian.ai/use-cases"},{"@type":"ListItem","position":3,"name":"Case Triage","item":"https://digian.ai/use-cases/case-triage"}]}} />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-sm font-medium tracking-wide text-indigo-600 uppercase">Customer Care / Case Triage &amp; Agent Assist</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900">
              AI Triage &amp; Agent Assist - Faster Response, Better Routing, Higher Throughput
            </h1>
            <p className="text-lg text-slate-600">
              AI automatically classifies inbound inquiries, extracts key fields, routes to the right team, and assists agents with suggested replies and next-best actions - across voice, chat, and email.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demo"><Button size="lg">Request a Demo</Button></Link>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              {['Real-time intent classification', 'Auto field extraction', 'Agent assist + auto-resolve'].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <BarChart2 className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-indigo-600 uppercase">Intelligent Case Triage</p>
                <p className="text-sm text-slate-600">Classify, extract, route, assist - at every touchpoint</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              {capabilities.map((item, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <BarChart2 className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-indigo-600 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <p className="text-3xl font-extrabold text-white">{s.value}</p>
                <p className="text-indigo-200 text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Why teams deploy AI triage and agent assist</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div key={b.title} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Capabilities</p>
            <h2 className="text-3xl font-bold text-slate-900">Everything needed for intelligent case triage</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Process</p>
            <h2 className="text-3xl font-bold text-slate-900">How AI triage works at runtime</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.step} {...fadeUp} transition={{ delay: i * 0.12 }}
                className="bg-white rounded-2xl border border-slate-200 p-8">
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-5xl font-black text-indigo-100">{s.step}</span>
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <BarChart2 className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">Classify, route, and assist - before a human touches it</h2>
            <p className="text-xl text-indigo-100">
              68% deflection. 88% routing accuracy. 35% more from every agent. Start with assist, scale to containment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo"><Button size="lg" variant="secondary">Request Demo</Button></Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Talk to Our Team <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <p className="text-indigo-200 text-sm">Intent classification · Field extraction · Agent assist · Auto-resolve</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseTriagePage;
